import React, {
  useEffect, useState, useMemo, useCallback
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { get } from 'lodash';
import { getRegionalConfig } from '../../config';
import { formatMoney } from '../../util/numberUtils';

const CurrencyInputMC = React.memo(({
  id,
  name,
  label,
  placeholder,
  className,
  onChange,
  onInput,
  value,
  type = 'text',
  textarea = false,
  useFloatingLabel = false,
  useFloatingAndNonFloatingLabel = false,
  containerClass = '',
  customError,
  hideBorder = false,
  disabled = false
}) => {
  const {
    control,
    formState: { errors },
    trigger
  } = useFormContext();

  const {
    number_of_decimals = 2,
    thousands_separator = ',',
    decimal_separator = '.'
  } = getRegionalConfig();

  const [focused, setFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState('');

  /* ---------------- FLAGS ---------------- */
  const showLabel = useMemo(
    () => (!useFloatingLabel || useFloatingAndNonFloatingLabel) && label,
    [useFloatingLabel, useFloatingAndNonFloatingLabel, label]
  );

  const showFloatingLabel = useMemo(
    () => useFloatingLabel || useFloatingAndNonFloatingLabel,
    [useFloatingLabel, useFloatingAndNonFloatingLabel]
  );

  const isColorType = type === 'color';

  /* ---------------- CLASSES ---------------- */
  const inputColorTypeClassNames = useMemo(() => [
    'tw-px-2',
    'tw-py-1',
    'tw-bg-white',
    'tw-rounded-lg',
    'tw-appearance-none',
    'focus:tw-outline-none',
    'focus:tw-ring-2',
    'focus:tw-ring-blue-400',
    'tw-text-sm',
    'tw-border',
    'tw-w-full',
    'tw-h-[42px]',
    className || ''
  ].join(' '), [className]);

  const inputClassNames = useMemo(() => [
    'tw-block',
    'tw-rounded-xl',
    'tw-px-4',
    showFloatingLabel ? 'tw-pt-5 tw-pb-0' : 'tw-py-3',
    'tw-w-full',
    'tw-text-default',
    'tw-bg-gray-50',
    'tw-border-gray-200',
    'tw-appearance-none',
    'focus:tw-outline-none',
    'focus:tw-ring-2',
    'focus:tw-ring-blue-500',
    'peer',
    hideBorder ? '' : 'tw-border',
    textarea ? 'tw-placeholder-blue-500' : '',
    className || ''
  ].join(' '), [
    showFloatingLabel,
    hideBorder,
    textarea,
    className
  ]);

  const floatingLabelClassNames = 'tw-absolute tw-text-xs tw-text-blue-500 tw-top-[5px] tw-z-10 tw-start-4';

  // Format for display while typing (with thousands separator, NO zero padding)
  const formatDisplayValue = useCallback((_value) => {
    if (!_value) return '';

    // First character must be a digit 0-9
    const stringValue = String(_value);
    if (stringValue && !/^[0-9]/.test(stringValue)) {
      return '';
    }

    // Convert dot to regional decimal separator
    const valueToFormat = stringValue.replace('.', decimal_separator);

    // If no decimals allowed, show only integer part
    if (number_of_decimals === 0) {
      // Split by decimal separator to get only integer part
      const parts = valueToFormat.split(decimal_separator);
      const integerOnly = parts[0].replace(/[^0-9]/g, '');
      return integerOnly.replace(/\B(?=(\d{3})+(?!\d))/g, thousands_separator);
    }

    // Separate integer and decimal using regional separator
    const parts = valueToFormat.split(decimal_separator);

    // Format integer part with thousands separator
    parts[0] = parts[0]
      .replace(/\D/g, '')
      .replace(/\B(?=(\d{3})+(?!\d))/g, thousands_separator);

    // Formatear parte decimal - NO rellenar con ceros mientras se tipea
    if (parts[1] !== undefined) {
      parts[1] = parts[1].replace(/\D/g, '').slice(0, number_of_decimals);
      return `${parts[0]}${decimal_separator}${parts[1]}`;
    }

    return parts[0];
  }, [decimal_separator, thousands_separator, number_of_decimals]);

  // Formato con relleno de ceros (SOLO para blur)
  const formatDisplayValueWithPadding = useCallback((_value) => {
    if (!_value) return '';

    // El valor ya fue validado, solo rellenar decimales con formatMoney
    const numValue = Number(_value);
    return isNaN(numValue) ? '' : formatMoney(numValue);
  }, []);

  /* ---------------- EFFECT ---------------- */
  useEffect(() => {
    if (value && !focused) {
      const formatted = formatDisplayValue(String(value));
      setDisplayValue(formatted);
    }
  }, [value, focused, formatDisplayValue]);

  /* ---------------- HELPERS ---------------- */

  // Convertir valor mostrado a formato backend (siempre con punto)
  const getBackendValue = useCallback((_displayValue) => {
    if (!_displayValue) return '';

    // Remover miles separator
    let cleaned = _displayValue.replace(new RegExp(`\\${thousands_separator}`, 'g'), '');

    // Si el separador decimal regional es coma, convertir a punto para backend
    if (decimal_separator !== '.') {
      cleaned = cleaned.replace(decimal_separator, '.');
    }

    return cleaned;
  }, [decimal_separator, thousands_separator]);

  /* ---------------- HANDLERS ---------------- */
  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleBlur = useCallback((event, field) => {
    setFocused(false);

    if (!displayValue.trim()) return;

    // Obtener valor limpio del backend
    let backendValue = getBackendValue(displayValue);

    // Rellenar con decimales si es necesario
    if (number_of_decimals > 0) {
      const parts = backendValue.split('.');
      const integerPart = parts[0] || '0';
      const decimalPart = (parts[1] || '').padEnd(number_of_decimals, '0').slice(0, number_of_decimals);
      backendValue = `${integerPart}.${decimalPart}`;
    }

    // Mostrar en formato regional CON RELLENO DE CEROS
    const formattedDisplay = formatDisplayValueWithPadding(backendValue);
    setDisplayValue(formattedDisplay);

    // Actualizar el value en React Hook Form
    field.onChange(backendValue);
    onChange?.({
      ...event,
      target: { ...event.target, value: backendValue, name: event.target.name }
    });
  }, [displayValue, getBackendValue, formatDisplayValueWithPadding, number_of_decimals, onChange]);

  const handleChange = useCallback((event, field) => {
    event.persist();
    if (disabled) return;

    // Normalizar value: remover thousands_separator y convertir decimal_separator a punto
    const inputValue = getBackendValue(event.target.value);

    // Si el valor está vacío, permitir (el usuario está borrando)
    if (!inputValue) {
      setDisplayValue('');
      field.onChange('');
      onChange?.({
        ...event,
        target: { ...event.target, value: '', name: event.target.name }
      });
      return;
    }

    // Si no permite decimales, solo permitir números
    if (number_of_decimals === 0) {
      const cleaned = inputValue.replace(/\D/g, '');
      const formatted = cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, thousands_separator);
      setDisplayValue(formatted);

      field.onChange(cleaned);
      onChange?.({
        ...event,
        target: { ...event.target, value: cleaned, name: event.target.name }
      });
      return;
    }

    // Formatear entrada y mostrar al usuario
    const formatted = formatDisplayValue(inputValue);

    // Si formatDisplayValue retorna vacío es porque comenzaba con un carácter inválido
    // En ese caso no actualizar el display (mantener lo que estaba)
    if (formatted === '' && inputValue !== '') {
      return;
    }

    setDisplayValue(formatted);

    // Convertir a formato backend
    const backendValue = getBackendValue(formatted);

    field.onChange(backendValue);
    onChange?.({
      ...event,
      target: { ...event.target, value: backendValue, name: event.target.name }
    });

    if (errors[name]) {
      trigger(name);
    }
  }, [
    disabled,
    number_of_decimals,
    thousands_separator,
    formatDisplayValue,
    getBackendValue,
    onChange,
    errors,
    name,
    trigger
  ]);

  /* ---------------- RENDER ---------------- */
  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      render={({ field }) => {
        // Sincronizar displayValue cuando field.value viene de setValue externo
        if (field.value && !focused && displayValue !== field.value) {
          const formatted = formatDisplayValue(String(field.value));
          if (formatted !== displayValue) {
            setDisplayValue(formatted);
          }
        }

        const inputProps = {
          className: isColorType
            ? inputColorTypeClassNames
            : inputClassNames,
          placeholder,
          id: id || name,
          type,
          onFocus: handleFocus,
          onBlur: (event) => handleBlur(event, field),
          onInput,
          onChange: (event) => handleChange(event, field),
          value: displayValue || field.value
        };

        return (
          <div className={`tw-wrapper ${containerClass}`}>
            {showLabel && (
              <label
                htmlFor={id || name}
                className={isColorType
                  ? 'tw-text-xs tw-text-blue-500 tw-mb-0.5'
                  : 'tw-text-zinc-700 tw-mb-1 tw-text-base tw-font-bold'}
              >
                {label}
              </label>
            )}

            <div className="tw-relative">
              <input {...field} {...inputProps} disabled={disabled} />
              {showFloatingLabel && (
                <label
                  htmlFor={id || name}
                  className={floatingLabelClassNames}
                >
                  {label}
                </label>
              )}
            </div>

            {get(errors, name) && (
              <p className="tw-text-xs tw-text-red-500">
                {get(errors, name).message}
              </p>
            )}

            {customError && (
              <p className="tw-text-xs tw-text-red-500">{customError}</p>
            )}
          </div>
        );
      }}
    />
  );
});

CurrencyInputMC.displayName = 'CurrencyInputMC';

export default CurrencyInputMC;
